import React from 'react';
import Table from '../../../core/form/Table';
import Button from '../../../core/form/Button';
import Row from '../../../core/layout/Row';
import PropTypes from 'prop-types';
import Async from '../../../core/layout/Async';
import { CONTRACT_PRODUCTS, HS_SKU_PLAN_PRODUCTS } from '../../../core/consts';
import Switch from '../../../core/form/Switch';
import get from 'lodash/get';

const realCurrencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const dollarCurrencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const UpfrontContractProductsList = (props) => {
  return (
    <Row>
      <Async fetching={props.loading}>
        <Table
          columns={[
            {
              label: 'Product',
              path: 'description',
            },
            {
              label: 'Amount',
              path: 'amount',
              width: '150px',
            },
            {
              label: 'Free amount',
              path: 'freeAmount',
              width: '150px',
              render: (cell, row) => (row.hs_sku === CONTRACT_PRODUCTS.CUSTOM_PACKAGE ? '-' : cell),
            },
            {
              label: 'Discount',
              path: 'discount',
              width: '100px',
              render: (cell) => (!!cell ? `${cell}%` : '-'),
            },
            {
              label: 'Unit Price',
              path: 'finalUnitPrice',
              width: '150px',
              render: (cell, row) => {
                if (
                  [CONTRACT_PRODUCTS.EXTRA_LICENSES_RANGE, CONTRACT_PRODUCTS.EXTRA_LICENSES_SEAT_RANGE].includes(
                    row.hs_sku
                  )
                ) {
                  const { min, max } = (row.finalUnitPriceRanges || []).reduce((acc, range) => {
                    if (!acc.min) acc.min = range.price;
                    if (!acc.max) acc.max = range.price;
                    if (range.price < acc.min) {
                      acc.min = range.price;
                    }
                    if (range.price > acc.max) {
                      acc.max = range.price;
                    }
                    return acc;
                  }, {});
                  return props.currencyType === 'real'
                    ? `${realCurrencyFormatter.format(min)} - ${realCurrencyFormatter.format(max)}`
                    : `${dollarCurrencyFormatter.format(min)} - ${dollarCurrencyFormatter.format(max)}`;
                }
                return props.currencyType === 'real'
                  ? realCurrencyFormatter.format(cell)
                  : dollarCurrencyFormatter.format(cell);
              },
            },
            ...(props.readOnly
              ? [
                  {
                    label: 'Net Unit Price',
                    path: 'finalUnitPrice',
                    width: '150px',
                    render: (cell, row) => {
                      let netValue = cell || 0;
                      let discountValue = 0;
                      if (row.discount) {
                        discountValue = (netValue * row.discount) / 100;
                      }
                      netValue = netValue - discountValue;

                      return props.currencyType === 'real'
                        ? realCurrencyFormatter.format(netValue)
                        : dollarCurrencyFormatter.format(netValue);
                    },
                  },
                ]
              : []),
            {
              label: 'Installments',
              path: 'installments',
              width: '150px',
            },
            {
              label: 'Paid Installments',
              path: 'paidInstallments',
              width: '150px',
            },
            ...(!props.readOnly
              ? [
                  {
                    label: 'Actions',
                    width: '150px',
                    render: (cell, row) => {
                      return (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          {![
                            ...(props.preserveCoreProducts ? [CONTRACT_PRODUCTS.CUSTOM_PACKAGE] : []),
                            CONTRACT_PRODUCTS.CANCELLATION_FINE,
                            CONTRACT_PRODUCTS.AI_EXERCISES,
                          ].includes(row.hs_sku) && (
                            <Button
                              icon="edit"
                              disabled={props.submitting || (row.hs_sku !== CONTRACT_PRODUCTS.OTHERS && !row.isEnabled)}
                              onClick={() => props.onEdit(row)}
                            />
                          )}
                          {![
                            CONTRACT_PRODUCTS.CUSTOM_PACKAGE,
                            CONTRACT_PRODUCTS.PLACEMENT_TEST_COMERCIAL,
                            CONTRACT_PRODUCTS.CANCELLATION_FINE,
                            CONTRACT_PRODUCTS.AI_EXERCISES,
                            CONTRACT_PRODUCTS.OTHERS,
                            ...HS_SKU_PLAN_PRODUCTS,
                          ].includes(row.hs_sku) && (
                            <div style={{ height: '40px' }}>
                              <Switch
                                label=""
                                titleOff="Disabled"
                                titleOn="Enabled"
                                onChange={() => props.onToggleProduct(row)}
                                value={get(row, 'isEnabled', false)}
                                disabled={props.submitting || props.loading || props.disabled}
                              />
                            </div>
                          )}
                          {(HS_SKU_PLAN_PRODUCTS.includes(row.hs_sku) ||
                            [CONTRACT_PRODUCTS.OTHERS].includes(row.hs_sku)) && (
                            <Button
                              icon="delete"
                              disabled={props.submitting || props.disabled}
                              onClick={() => props.onDelete(row)}
                            />
                          )}
                        </div>
                      );
                    },
                  },
                ]
              : []),
          ]}
          rows={props.data}
          rowKey="rowKey"
        />
      </Async>
    </Row>
  );
};

UpfrontContractProductsList.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      amount: PropTypes.number,
      installments: PropTypes.number,
      finalUnitPrice: PropTypes.string.isRequired,
    })
  ),
  readOnly: PropTypes.bool,
  preserveCoreProducts: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  submitting: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  currencyType: PropTypes.oneOf(['real', 'dollar']),
};

UpfrontContractProductsList.defaultProps = {
  data: [],
  submitting: false,
  readOnly: false,
  loading: false,
  currencyType: 'real',
};

export default UpfrontContractProductsList;
